/**
 * The torn-paper collage behind the hero, after the first panel of the source
 * site (craft.do, looked at 2026-10-14): a sky that pales toward the ground, a
 * periwinkle sheet rising from the left, a white and an ink scrap in the
 * middle distance, a ruled yellow notebook page leaning in from the right,
 * and two clouds drifting in from the edges.
 *
 * Their collage is photographs of paper and is theirs; this one is four
 * clipped rectangles and two blurred clusters of discs, in our own tokens. Decoration only: it carries no
 * meaning, takes no pointer events and is hidden from assistive technology.
 */

/** mulberry32: the same tear on every render, on the server of a test and on a phone. */
function seeded(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A torn edge: a slow profile (the shape of the sheet) with a fast, small
 * wobble on top (the fibres). Sixty points read as paper; twenty read as a saw.
 * Coordinates are percentages of the sheet's own box.
 */
function edge(profile: (x: number) => number, seed: number, reverse = false): string[] {
  const random = seeded(seed);
  const points: string[] = [];
  const STEPS = 60;
  for (let step = 0; step <= STEPS; step += 1) {
    const x = (step / STEPS) * 100;
    const y = Math.min(100, Math.max(0, profile(x) + (random() - 0.5) * 3.2));
    points.push(`${x.toFixed(1)}% ${y.toFixed(1)}%`);
  }
  return reverse ? points.reverse() : points;
}

const sheet = (profile: (x: number) => number, seed: number): string =>
  `polygon(${[...edge(profile, seed), '100% 100%', '0 100%'].join(', ')})`;

/** A low hill, highest two thirds of the way along. */
const HILL = sheet((x) => 30 - 24 * Math.sin((x / 100) * Math.PI * 0.85 + 0.25), 7);
/** A mountain behind the picture. */
const PEAK = sheet((x) => 70 * (1 - Math.exp(-(((x - 56) / 30) ** 2))) + 2, 19);
/** A scrap torn on both long sides. */
const SCRAP = `polygon(${[
  ...edge((x) => 22 - 14 * Math.sin((x / 100) * Math.PI), 31),
  ...edge((x) => 84 + 8 * Math.sin((x / 100) * Math.PI * 2), 43, true),
].join(', ')})`;

/** Notebook rules: one hairline of ink every 28px. */
const RULED =
  'repeating-linear-gradient(to bottom, transparent 0 27px, rgb(3 3 2 / 0.16) 27px 28px)';

/**
 * A cloud is seven white discs under one blur: the discs give it lumps, the
 * blur takes the circles away. `className` places and sizes the box.
 */
const PUFFS = [
  'left-[4%] top-[46%] h-[46%] w-[30%]',
  'left-[20%] top-[24%] h-[62%] w-[30%]',
  'left-[38%] top-[6%] h-[80%] w-[32%]',
  'left-[58%] top-[28%] h-[58%] w-[28%]',
  'left-[72%] top-[48%] h-[42%] w-[24%]',
  'left-[10%] top-[62%] h-[34%] w-[80%]',
  'left-[30%] top-[40%] h-[50%] w-[44%]',
];

function Cloud({ className }: { className: string }) {
  return (
    <div className={`absolute aspect-[2/1] opacity-90 blur-xl ${className}`}>
      {PUFFS.map((puff) => (
        <span key={puff} className={`absolute rounded-full bg-surface-container-lowest ${puff}`} />
      ))}
    </div>
  );
}

export function HeroCollage() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <Cloud className="-left-[14%] top-[30%] w-[62%] sm:-left-[6%] sm:top-[30%] sm:w-[34%]" />
      <Cloud className="-right-[18%] top-[9%] w-[58%] sm:-right-[5%] sm:top-[13%] sm:w-[30%]" />
      <div
        className="absolute bottom-0 left-[6%] h-[30%] w-[92%] bg-surface-container-lowest sm:left-[20%] sm:h-[64%] sm:w-[72%]"
        style={{ clipPath: PEAK }}
      />
      <div
        className="absolute bottom-[20%] right-0 h-[7%] w-[30%] bg-primary sm:bottom-[30%] sm:h-[13%] sm:w-[20%]"
        style={{ clipPath: SCRAP }}
      />
      <div
        className="absolute -left-[4%] bottom-0 h-[24%] w-[70%] bg-panel-periwinkle sm:h-[46%] sm:w-[44%]"
        style={{ clipPath: HILL }}
      />
      <div
        className="absolute -bottom-[3%] -right-[8%] h-[18%] w-[52%] origin-bottom-right -rotate-6 bg-panel-sun sm:h-[34%] sm:w-[28%]"
        style={{ backgroundImage: RULED }}
      />
    </div>
  );
}
